import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getSouscriptions, exportSouscriptions, deleteSouscription, restoreSouscription, livrerSouscription, verifyPaymentEligibility, checkDocumentLivrable } from '../../services/souscriptionService';
import api from '../../services/api';
import { getLogementsDisponibles } from '../../services/logementService';
import { previewAttestation, previewPdf, sendProformaEmail } from '../../services/proformaService';
import WizardSouscription from './WizardSouscription';
import SouscriptionViewModal from './SouscriptionViewModal';
import SouscriptionCard from './SouscriptionCard';
import PaiementModal from './PaiementModal';
import PaymentMethodModal from './PaymentMethodModal';
import { stripePaymentService } from '../../services/stripePaymentService';
import ConfirmModal from '../common/ConfirmModal';
import ConfirmationModal from '../common/ConfirmationModal';
import PaymentErrorModal from './PaymentErrorModal';
import { LivraisonSuccessModal, LivraisonErrorModal, LivraisonLoader } from './LivraisonModal';
import { useAuth } from '../../contexts/AuthContext';
import { useSouscriptionUpdates } from '../../hooks/useWebSocket';

const FILTER_KEYS = [
  'statut',
  'search',
  'date_debut',
  'date_fin',
  'pays_destination',
  'ville_ecole',
  'ville_logement',
  'created_by'
];

const DEBOUNCED_FILTER_KEYS = [
  'search',
  'date_debut',
  'date_fin',
  'pays_destination',
  'ville_ecole',
  'ville_logement',
  'created_by'
];

const ADVANCED_FILTER_KEYS = FILTER_KEYS.filter((key) => key !== 'search');

const INITIAL_FILTERS = {
  skip: 0,
  limit: 10,
  statut: '',
  search: '',
  date_debut: '',
  date_fin: '',
  pays_destination: '',
  ville_ecole: '',
  ville_logement: '',
  created_by: ''
};

const buildSouscriptionParams = (filters, { includePagination = true } = {}) => {
  const params = {};

  FILTER_KEYS.forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      params[key] = typeof value === 'string' ? value.trim() : value;
    }
  });

  if (includePagination) {
    params.skip = filters.skip;
    params.limit = filters.limit;
  }

  return params;
};

const HistoriqueSection = ({ onDataChange }) => {
  const { user } = useAuth();
  const [souscriptions, setSouscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exportingFormat, setExportingFormat] = useState(null);
  const requestSequenceRef = useRef(0);
  const hasLoadedRef = useRef(false);

  // États pour la pagination
  const [paginationFilters, setPaginationFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const appliedFiltersRef = useRef(INITIAL_FILTERS);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedSouscription, setSelectedSouscription] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [souscriptionToEdit, setSouscriptionToEdit] = useState(null);
  const [showEditWizard, setShowEditWizard] = useState(false);
  
  // États pour l'envoi de proforma
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailModalConfig, setEmailModalConfig] = useState({
    type: 'info',
    title: '',
    message: '',
    onConfirm: null
  });

  // États pour les modals de paiement
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [showPaiementModal, setShowPaiementModal] = useState(false);
  const [souscriptionToPay, setSouscriptionToPay] = useState(null);

  // États pour les modals de livraison
  const [showLivraisonLoader, setShowLivraisonLoader] = useState(false);
  const [showLivraisonSuccess, setShowLivraisonSuccess] = useState(false);
  const [showLivraisonError, setShowLivraisonError] = useState(false);
  const [livraisonData, setLivraisonData] = useState({
    souscriptionReference: '',
    documentPath: '',
    downloadFilename: '',
    errorMessage: ''
  });

  // États pour le modal de confirmation de livraison
  const [showLivraisonConfirm, setShowLivraisonConfirm] = useState(false);
  const [souscriptionToDeliver, setSouscriptionToDeliver] = useState(null);

  // États pour le modal d'erreur de paiement
  const [showPaymentError, setShowPaymentError] = useState(false);
  const [paymentErrorData, setPaymentErrorData] = useState({
    errorType: '',
    souscriptionReference: '',
    errorMessage: ''
  });

  const [restoreModal, setRestoreModal] = useState({
    show: false,
    souscription: null,
    logements: [],
    selectedLogementId: '',
    errorMessage: '',
    loading: false
  });

  const loadSouscriptions = useCallback(async (filters, signal) => {
    const requestId = ++requestSequenceRef.current;
    const isInitialLoad = !hasLoadedRef.current;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const data = await getSouscriptions(
        buildSouscriptionParams(filters),
        signal ? { signal } : {}
      );

      if (requestId === requestSequenceRef.current) {
        setSouscriptions(data);
      }
    } catch (error) {
      const requestWasCancelled = error.code === 'ERR_CANCELED' || error.name === 'CanceledError';
      if (!requestWasCancelled) {
        console.error('Erreur lors du chargement des souscriptions:', error);
      }
    } finally {
      if (requestId === requestSequenceRef.current) {
        hasLoadedRef.current = true;
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  const searchFilter = paginationFilters.search;
  const startDateFilter = paginationFilters.date_debut;
  const endDateFilter = paginationFilters.date_fin;
  const destinationCountryFilter = paginationFilters.pays_destination;
  const schoolCityFilter = paginationFilters.ville_ecole;
  const housingCityFilter = paginationFilters.ville_logement;
  const createdByFilter = paginationFilters.created_by;

  // Les champs saisissables restent locaux pendant la frappe, puis sont appliqués après une courte pause.
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setAppliedFilters((currentFilters) => {
        const debouncedValues = {
          search: searchFilter,
          date_debut: startDateFilter,
          date_fin: endDateFilter,
          pays_destination: destinationCountryFilter,
          ville_ecole: schoolCityFilter,
          ville_logement: housingCityFilter,
          created_by: createdByFilter
        };
        const nextFilters = { ...currentFilters, skip: 0 };
        DEBOUNCED_FILTER_KEYS.forEach((key) => {
          nextFilters[key] = debouncedValues[key];
        });

        const filtersUnchanged = DEBOUNCED_FILTER_KEYS.every(
          (key) => currentFilters[key] === nextFilters[key]
        ) && currentFilters.skip === nextFilters.skip;

        return filtersUnchanged ? currentFilters : nextFilters;
      });
    }, 450);

    return () => window.clearTimeout(timeoutId);
  }, [
    searchFilter,
    startDateFilter,
    endDateFilter,
    destinationCountryFilter,
    schoolCityFilter,
    housingCityFilter,
    createdByFilter
  ]);

  useEffect(() => {
    const controller = new AbortController();
    loadSouscriptions(appliedFilters, controller.signal);

    return () => controller.abort();
  }, [appliedFilters, loadSouscriptions]);

  // Écouter les mises à jour WebSocket des souscriptions
  const handleSouscriptionUpdate = useCallback(() => {
    // Ne pas recharger si l'utilisateur est en train de travailler sur un modal
    const isUserWorking = showEditWizard || showViewModal || showEmailModal || showPaymentMethodModal || showPaiementModal;

    if (isUserWorking) {
      console.log('⚠️ Modal ouvert - Mise à jour WebSocket ignorée pour préserver le travail en cours');
      return;
    }

    console.log('🔔 Mise à jour de l\'historique suite à changement souscription');
    loadSouscriptions(appliedFiltersRef.current);
  }, [
    loadSouscriptions,
    showEditWizard,
    showViewModal,
    showEmailModal,
    showPaymentMethodModal,
    showPaiementModal
  ]);

  useSouscriptionUpdates(handleSouscriptionUpdate);

  const handleResetFilters = () => {
    const resetFilters = {
      ...paginationFilters,
      skip: 0,
      statut: '',
      search: '',
      date_debut: '',
      date_fin: '',
      pays_destination: '',
      ville_ecole: '',
      ville_logement: '',
      created_by: ''
    };

    setPaginationFilters(resetFilters);
    setAppliedFilters(resetFilters);
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async (format) => {
    try {
      setExportingFormat(format);
      const { blob, filename } = await exportSouscriptions(
        format,
        buildSouscriptionParams(paginationFilters, { includePagination: false })
      );
      downloadBlob(blob, filename);
    } catch (error) {
      console.error('Erreur export souscriptions:', error);
      const message = error.response?.data?.detail || 'Impossible de générer l\'export demandé.';
      alert(message);
    } finally {
      setExportingFormat(null);
    }
  };

  // Gestion de la pagination
  const handlePaginationChange = (key, value) => {
    const isPaginationChange = key === 'skip';
    const isImmediateFilter = key === 'skip' || key === 'limit' || key === 'statut';

    setPaginationFilters((currentFilters) => ({
      ...currentFilters,
      [key]: value,
      skip: isPaginationChange ? value : 0
    }));

    if (isImmediateFilter) {
      setAppliedFilters((currentFilters) => ({
        ...currentFilters,
        [key]: value,
        skip: isPaginationChange ? value : 0
      }));
    }
  };

  // Fonction pour recharger et revenir à la première page
  const reloadFromFirstPage = async () => {
    setPaginationFilters(prev => ({ ...prev, skip: 0 }));
    const filters = { ...appliedFiltersRef.current, skip: 0 };

    if (appliedFiltersRef.current.skip === 0) {
      await loadSouscriptions(filters);
    } else {
      setAppliedFilters(filters);
    }
  };

  const handleView = (souscription) => {
    setSelectedSouscription(souscription);
    setShowViewModal(true);
  };

  const handleModifier = (souscription) => {
    setSouscriptionToEdit(souscription);
    setShowEditWizard(true);
  };

  const handleEditComplete = async () => {
    setShowEditWizard(false);
    setSouscriptionToEdit(null);
    await reloadFromFirstPage(); // Revenir à la première page pour voir la souscription modifiée
    onDataChange();
  };

  const handleEditCancel = () => {
    setShowEditWizard(false);
    setSouscriptionToEdit(null);
  };

  const handlePayer = async (souscription) => {
    try {
      // Vérifier l'éligibilité au paiement
      await verifyPaymentEligibility(souscription.id);

      // Si la vérification réussit, ouvrir le modal de choix de méthode
      setSouscriptionToPay(souscription);
      setShowPaymentMethodModal(true);
      
    } catch (error) {
      console.error('Erreur vérification paiement:', error);
      
      // Analyser le type d'erreur
      let errorType = 'general';
      let errorMessage = 'Une erreur s\'est produite lors de la vérification.';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail.toLowerCase();
        
        if (detail.includes('aucun logement associé')) {
          errorType = 'no_logement';
        } else if (detail.includes('plus disponible') || detail.includes('occupé')) {
          errorType = 'logement_unavailable';
        } else {
          errorMessage = error.response.data.detail;
        }
      }
      
      // Afficher le modal d'erreur spécifique
      setPaymentErrorData({
        errorType,
        souscriptionReference: souscription.reference,
        errorMessage
      });
      setShowPaymentError(true);
    }
  };

  const handlePaiementSuccess = async (updatedSouscription) => {
    setSouscriptionToPay(null);
    await reloadFromFirstPage(); // Revenir à la première page pour voir la modification
    onDataChange();
  };

  const handlePaymentMethodModalClose = () => {
    setSouscriptionToPay(null);
    setShowPaymentMethodModal(false);
  };

  const handlePaymentMethodSelect = async (method, souscription) => {
    // Fermer le modal de choix de méthode
    setShowPaymentMethodModal(false);

    if (method === 'manual') {
      // Ouvrir immédiatement le modal de paiement manuel existant
      // La souscriptionToPay est déjà définie, on ne la remet pas à null
      setShowPaiementModal(true);
    } else if (method === 'online') {
      try {
        console.log('🔮 Création du paiement en ligne Stripe pour souscription:', souscriptionToPay.id);

        // Créer et rediriger vers Stripe automatiquement
        // Ne pas surcharger les URLs, laisser le backend gérer avec {CHECKOUT_SESSION_ID}
        const result = await stripePaymentService.creerEtRedirigerVersStripe(souscriptionToPay.id, {
          // Le montant sera calculé automatiquement côté backend
          // Les URLs success/cancel sont gérées par défaut dans le backend
        });

        if (!result.success) {
          throw new Error(result.error);
        }

        // La redirection a déjà eu lieu dans creerEtRedirigerVersStripe
        // On nettoie les états locaux
        setSouscriptionToPay(null);

      } catch (error) {
        console.error('Erreur paiement en ligne:', error);

        // Afficher une erreur à l'utilisateur
        setPaymentErrorData({
          errorType: 'stripe_creation_error',
          souscriptionReference: souscriptionToPay.reference,
          errorMessage: `Erreur lors de la création du paiement Stripe: ${error.message}`
        });
        setShowPaymentError(true);
        setSouscriptionToPay(null);
      }
    }
  };

  const handlePaiementModalClose = () => {
    setSouscriptionToPay(null);
    setShowPaiementModal(false);
  };

  const handleLivrer = (souscription) => {
    setSouscriptionToDeliver(souscription);
    setShowLivraisonConfirm(true);
  };

  const confirmLivraison = async () => {
    if (!souscriptionToDeliver) return;
    
    try {
      // Fermer le modal de confirmation
      setShowLivraisonConfirm(false);
      
      // Afficher le loader
      setLivraisonData({
        souscriptionReference: souscriptionToDeliver.reference,
        documentPath: '',
        downloadFilename: '',
        errorMessage: ''
      });
      setShowLivraisonLoader(true);

      // Appeler le service de livraison
      const result = await livrerSouscription(souscriptionToDeliver.id);
      
      // Masquer le loader
      setShowLivraisonLoader(false);

      if (result.success) {
        // Mettre à jour les données de livraison pour le modal de succès
        setLivraisonData(prev => ({
          ...prev,
          documentPath: `/api/souscriptions/${souscriptionToDeliver.id}/document-livrable`,
          downloadFilename: result.download_filename || `attestation_${souscriptionToDeliver.reference}.pdf`
        }));
        
        // Recharger les données depuis la première page
        await reloadFromFirstPage();
        onDataChange();
        
        // Afficher le modal de succès
        setShowLivraisonSuccess(true);
      } else {
        // Afficher le modal d'erreur
        setLivraisonData(prev => ({
          ...prev,
          errorMessage: result.message || 'Erreur lors de la livraison'
        }));
        setShowLivraisonError(true);
      }
    } catch (error) {
      // Masquer le loader en cas d'erreur
      setShowLivraisonLoader(false);
      
      // Afficher le modal d'erreur
      const errorMessage = error.response?.status === 400 
        ? (error.response.data.detail || error.message)
        : `Erreur lors de la livraison: ${error.message}`;
        
      setLivraisonData(prev => ({
        ...prev,
        errorMessage: errorMessage
      }));
      setShowLivraisonError(true);
    } finally {
      setSouscriptionToDeliver(null);
    }
  };

  const getApiErrorMessage = (error) => {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
    return error.message;
  };

  const handleDelete = async (souscription) => {
    if (window.confirm('Supprimer logiquement cette souscription ? Elle restera visible dans l’historique et pourra être rétablie.')) {
      try {
        await deleteSouscription(souscription.id);
        await reloadFromFirstPage();
        onDataChange();
      } catch (error) {
        alert('Erreur lors de la suppression: ' + getApiErrorMessage(error));
      }
    }
  };

  const handleRestore = async (souscription) => {
    if (!window.confirm(`Rétablir la souscription ${souscription.reference} ?`)) {
      return;
    }

    try {
      await restoreSouscription(souscription.id);
      await reloadFromFirstPage();
      onDataChange();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.code === 'LOGEMENT_INDISPONIBLE' || detail?.code === 'LOGEMENT_INTROUVABLE') {
        try {
          const logements = await getLogementsDisponibles();
          setRestoreModal({
            show: true,
            souscription,
            logements,
            selectedLogementId: '',
            errorMessage: detail.message,
            loading: false
          });
        } catch (loadError) {
          alert(`${detail.message} Impossible de charger les logements disponibles: ${loadError.message}`);
        }
        return;
      }

      alert('Erreur lors du rétablissement: ' + getApiErrorMessage(error));
    }
  };

  const handleRestoreWithLogement = async () => {
    if (!restoreModal.selectedLogementId) {
      setRestoreModal(prev => ({ ...prev, errorMessage: 'Veuillez sélectionner un logement disponible.' }));
      return;
    }

    try {
      setRestoreModal(prev => ({ ...prev, loading: true }));
      await restoreSouscription(restoreModal.souscription.id, parseInt(restoreModal.selectedLogementId, 10));
      setRestoreModal({ show: false, souscription: null, logements: [], selectedLogementId: '', errorMessage: '', loading: false });
      await reloadFromFirstPage();
      onDataChange();
    } catch (error) {
      setRestoreModal(prev => ({
        ...prev,
        loading: false,
        errorMessage: getApiErrorMessage(error)
      }));
    }
  };

  const handlePreviewAttestation = async (souscription) => {
    try {
      // Vérifier si la souscription a déjà été livrée
      if (souscription.statut === 'LIVRE') {
        try {
          // Vérifier si le document livrable existe
          const documentCheck = await checkDocumentLivrable(souscription.id);
          
          if (documentCheck.exists) {
            // Ouvrir le document déjà généré pendant la livraison en utilisant l'API service
            try {
              const response = await api.get(`/souscriptions/${souscription.id}/document-livrable`, {
                responseType: 'blob',
                headers: {
                  'Accept': 'application/pdf',
                },
              });

              if (response.data) {
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const newWindow = window.open(url, '_blank');

                // Nettoyer l'URL après un délai
                setTimeout(() => {
                  window.URL.revokeObjectURL(url);
                }, 1000);

                if (!newWindow) {
                  throw new Error('Impossible d\'ouvrir un nouvel onglet. Vérifiez que les popups ne sont pas bloqués.');
                }
                return;
              }
            } catch (error) {
              console.warn('Erreur lors de l\'ouverture du document livrable, basculement vers preview:', error);
            }
          }
        } catch (error) {
          console.warn('Document livrable non accessible, génération d\'un nouveau preview:', error);
        }
      }
      
      // Générer un nouveau document de preview (pour souscriptions non livrées ou si document livrable indisponible)
      const result = await previewAttestation(souscription.id);
      if (result.success) {
        previewPdf(result.pdfUrl);
      } else {
        alert('Erreur lors de la génération: ' + result.error);
      }
    } catch (error) {
      alert('Erreur lors de la génération de l\'attestation: ' + error.message);
    }
  };

  const handleSendProforma = (souscription) => {
    // Configuration du modal de confirmation
    setEmailModalConfig({
      type: 'info',
      title: 'Envoyer Proforma',
      message: `Envoyer le proforma pour ${souscription.prenom_client} ${souscription.nom_client} à l'adresse ${souscription.email_client} ?`,
      onConfirm: () => performSendProforma(souscription)
    });
    setShowEmailModal(true);
  };

  const performSendProforma = async (souscription) => {
    try {
      setEmailLoading(true);
      
      const result = await sendProformaEmail(souscription.id);
      
      if (result.success) {
        // Modal de succès
        setEmailModalConfig({
          type: 'success',
          title: 'Envoi réussi !',
          message: `Le proforma a été envoyé avec succès à ${result.recipient}. Référence: ${result.reference}`,
          onConfirm: () => setShowEmailModal(false)
        });
      } else {
        // Modal d'erreur
        setEmailModalConfig({
          type: 'error',
          title: 'Erreur d\'envoi',
          message: result.error || 'Une erreur est survenue lors de l\'envoi du proforma.',
          onConfirm: () => setShowEmailModal(false)
        });
      }
      
    } catch (error) {
      // Modal d'erreur pour les exceptions
      setEmailModalConfig({
        type: 'error',
        title: 'Erreur système',
        message: `Erreur inattendue: ${error.message}`,
        onConfirm: () => setShowEmailModal(false)
      });
    } finally {
      setEmailLoading(false);
    }
  };

  const getStatutBadge = (statut) => {
    const styles = {
      'ATTENTE_PAIEMENT': 'bg-red-100 text-red-800',
      'ATTENTE_LIVRAISON': 'bg-blue-100 text-blue-800',
      'LIVRE': 'bg-green-100 text-green-800',
      'CLOTURE': 'bg-gray-100 text-gray-800',
      'SUPPRIMEE': 'bg-orange-100 text-orange-800'
    };
    
    const labels = {
      'ATTENTE_PAIEMENT': 'Attente paiement',
      'ATTENTE_LIVRAISON': 'Attente livraison',
      'LIVRE': 'Livré',
      'CLOTURE': 'Clôturé',
      'SUPPRIMEE': 'Supprimée'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[statut] || 'bg-gray-100 text-gray-800'}`}>
        {labels[statut] || statut}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const activeAdvancedFilterCount = ADVANCED_FILTER_KEYS.filter((key) => {
    const value = paginationFilters[key];
    return value !== undefined && value !== null && String(value).trim() !== '';
  }).length;

  return (
    <>
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Historique des Souscriptions</h2>
            <p className="text-sm text-gray-600">Gérez vos souscriptions existantes</p>
          </div>
          {refreshing && (
            <div role="status" className="flex items-center gap-2 text-sm text-blue-600 whitespace-nowrap">
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></span>
              Actualisation...
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-b border-blue-100 bg-blue-50/40">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-blue-700 mb-1">Recherche</label>
              <input
                type="text"
                value={paginationFilters.search}
                onChange={(e) => handlePaginationChange('search', e.target.value)}
                placeholder="Nom, email, référence, école, logement..."
                className="w-full text-sm border border-blue-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>

            <button
              type="button"
              aria-expanded={showAdvancedFilters}
              onClick={() => setShowAdvancedFilters((currentValue) => !currentValue)}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-200 rounded-md hover:bg-blue-50 transition-colors whitespace-nowrap"
            >
              <span>{showAdvancedFilters ? 'Réduire les filtres' : 'Afficher plus de filtres'}</span>
              {!showAdvancedFilters && activeAdvancedFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 text-xs font-semibold text-white bg-orange-500 rounded-full">
                  {activeAdvancedFilterCount}
                </span>
              )}
              <svg
                className={`w-4 h-4 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
          </div>

          {showAdvancedFilters && (
            <div className="mt-4 pt-4 border-t border-blue-100 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-blue-700 mb-1">Statut</label>
                <select
                  value={paginationFilters.statut}
                  onChange={(e) => handlePaginationChange('statut', e.target.value)}
                  className="w-full text-sm border border-blue-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">Tous</option>
                  <option value="ATTENTE_PAIEMENT">En attente paiement</option>
                  <option value="ATTENTE_LIVRAISON">En attente livraison</option>
                  <option value="LIVRE">Livré</option>
                  <option value="CLOTURE">Clôturé</option>
                  <option value="SUPPRIMEE">Supprimée</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-blue-700 mb-1">Créé par</label>
                <input
                  type="text"
                  value={paginationFilters.created_by}
                  onChange={(e) => handlePaginationChange('created_by', e.target.value)}
                  placeholder="Nom ou email utilisateur"
                  className="w-full text-sm border border-blue-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-blue-700 mb-1">Date début</label>
                <input
                  type="date"
                  value={paginationFilters.date_debut}
                  onChange={(e) => handlePaginationChange('date_debut', e.target.value)}
                  className="w-full text-sm border border-blue-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-blue-700 mb-1">Date fin</label>
                <input
                  type="date"
                  value={paginationFilters.date_fin}
                  onChange={(e) => handlePaginationChange('date_fin', e.target.value)}
                  className="w-full text-sm border border-blue-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-blue-700 mb-1">Pays destination</label>
                <input
                  type="text"
                  value={paginationFilters.pays_destination}
                  onChange={(e) => handlePaginationChange('pays_destination', e.target.value)}
                  placeholder="France, Canada..."
                  className="w-full text-sm border border-blue-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-blue-700 mb-1">Ville école</label>
                <input
                  type="text"
                  value={paginationFilters.ville_ecole}
                  onChange={(e) => handlePaginationChange('ville_ecole', e.target.value)}
                  placeholder="Malakoff, Toulouse..."
                  className="w-full text-sm border border-blue-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-blue-700 mb-1">Ville logement</label>
                <input
                  type="text"
                  value={paginationFilters.ville_logement}
                  onChange={(e) => handlePaginationChange('ville_logement', e.target.value)}
                  placeholder="Paris, Nantes..."
                  className="w-full text-sm border border-blue-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleResetFilters}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-200 rounded-md hover:bg-blue-50 transition-colors"
              >
                Réinitialiser
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {['csv', 'excel', 'pdf'].map((format) => (
                <button
                  key={format}
                  onClick={() => handleExport(format)}
                  disabled={!!exportingFormat}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-orange-500 rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {exportingFormat === format ? 'Export...' : `Export ${format.toUpperCase()}`}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Les filtres restent montés pendant le chargement ; seuls les résultats changent. */}
        {loading ? (
          <div className="p-8 text-center text-gray-500" role="status">
            <span className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></span>
            <p className="mt-2">Chargement des souscriptions...</p>
          </div>
        ) : souscriptions.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <div className="w-12 h-12 mx-auto mb-4 text-gray-400">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </div>
            <p>Aucune souscription pour le moment</p>
            <p className="text-sm">Utilisez le service ci-dessus pour créer votre première souscription</p>
          </div>
        ) : (
          <>
            {/* VUE TABLEAU - DESKTOP (écrans larges) */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Logement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date création
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {souscriptions.map((souscription) => (
                  <tr key={souscription.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {souscription.prenom_client} {souscription.nom_client}
                        </div>
                        <div className="text-sm text-gray-500">
                          {souscription.email_client}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {souscription.logement?.titre || 'Non défini'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {souscription.logement?.loyer ? `${souscription.logement.loyer}€/mois` : ''}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {souscription.logement?.ville && souscription.logement?.code_postal ? (
                            `${souscription.logement.ville} ${souscription.logement.code_postal}`
                          ) : (
                            'Localisation non définie'
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(souscription.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatutBadge(souscription.statut)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleView(souscription)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                          </svg>
                          Voir
                        </button>
                        {/* DEBUG: Afficher les conditions */}
                        {/* {console.log(`🔍 DEBUG Souscription ${souscription.id}:`, {
                          statut: souscription.statut,
                          userRole: user?.role,
                          modifierVisible: (souscription.statut === 'ATTENTE_PAIEMENT' || souscription.statut === 'ATTENTE_LIVRAISON'),
                          payerVisible: souscription.statut === 'ATTENTE_PAIEMENT',
                          livrerVisible: user?.role === 'admin-generale' && souscription.statut === 'ATTENTE_LIVRAISON',
                          previewAttestationVisible: user?.role === 'admin-generale'
                        })} */}
                        {/* Bouton MODIFIER selon les nouvelles règles :
                            - ATTENTE_PAIEMENT : visible
                            - ATTENTE_LIVRAISON : visible  
                            - LIVRE : masqué */}
                        {(souscription.statut === 'ATTENTE_PAIEMENT' || souscription.statut === 'ATTENTE_LIVRAISON') && (
                          <button
                            onClick={() => handleModifier(souscription)}
                            className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-orange-700 bg-orange-100 hover:bg-orange-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                            </svg>
                            Modifier
                          </button>
                        )}
                        {/* Bouton ACTION PAYER selon les règles clarifiées :
                            - ATTENTE_PAIEMENT : visible (pour effectuer le paiement)
                            - PAYE : masqué (déjà payé)
                            - LIVRE : masqué */}
                        {souscription.statut === 'ATTENTE_PAIEMENT' && (
                          <button
                            onClick={() => handlePayer(souscription)}
                            className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
                            </svg>
                            Payer
                          </button>
                        )}
                        {/* Bouton ACTION LIVRER selon les nouvelles règles :
                            - ATTENTE_PAIEMENT : masqué
                            - ATTENTE_LIVRAISON : visible UNIQUEMENT pour ADMIN-GENERALE (PAS pour agent-boaz)
                            - LIVRE : masqué (déjà livré) */}
                        {user?.role === 'admin-generale' && souscription.statut === 'ATTENTE_LIVRAISON' && (
                          <button
                            onClick={() => handleLivrer(souscription)}
                            className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-purple-700 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                            Livrer
                          </button>
                        )}
                        {souscription.statut !== 'SUPPRIMEE' && (
                          <button
                            onClick={() => handleSendProforma(souscription)}
                            className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"></path>
                            </svg>
                            Envoyer Proforma
                          </button>
                        )}
                        {/* Bouton Preview Attestation - EXCLUSIVEMENT pour ADMIN-GENERALE (PAS pour agent-boaz) et non livré/clôturé */}
                        {user?.role === 'admin-generale' && souscription.statut !== 'LIVRE' && souscription.statut !== 'CLOTURE' && souscription.statut !== 'SUPPRIMEE' && (
                          <button
                            onClick={() => handlePreviewAttestation(souscription)}
                            className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-purple-700 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                            </svg>
                            Preview Attestation
                          </button>
                        )}
                        {/* Bouton Supprimer selon les règles :
                            - Visible UNIQUEMENT pour ADMIN-GENERALE (PAS pour agent-boaz) */}
                        {user?.role === 'admin-generale' && souscription.statut !== 'SUPPRIMEE' && (
                          <button
                            onClick={() => handleDelete(souscription)}
                            className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                          >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                          </svg>
                          Supprimer
                          </button>
                        )}
                        {user?.role === 'admin-generale' && souscription.statut === 'SUPPRIMEE' && (
                          <button
                            onClick={() => handleRestore(souscription)}
                            className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                            </svg>
                            Rétablir
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* VUE CARDS - MOBILE/TABLET (petits écrans) */}
          <div className="block lg:hidden p-4">
            <div className="grid gap-4">
              {souscriptions.map((souscription) => (
                <SouscriptionCard
                  key={souscription.id}
                  souscription={souscription}
                  user={user}
                  onView={handleView}
                  onModifier={handleModifier}
                  onPayer={handlePayer}
                  onLivrer={handleLivrer}
                  onSendProforma={handleSendProforma}
                  onPreviewAttestation={handlePreviewAttestation}
                  onDelete={handleDelete}
                  onRestore={handleRestore}
                  getStatutBadge={getStatutBadge}
                  formatDate={formatDate}
                />
              ))}
            </div>
          </div>
        </>
        )}

        {/* Pagination responsive pour les souscriptions */}
        {!loading && souscriptions.length > 0 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            {/* Version desktop */}
            <div className="hidden lg:flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePaginationChange('skip', Math.max(0, paginationFilters.skip - paginationFilters.limit))}
                  disabled={paginationFilters.skip === 0}
                  className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                  </svg>
                  Précédent
                </button>
                <button
                  onClick={() => handlePaginationChange('skip', paginationFilters.skip + paginationFilters.limit)}
                  disabled={souscriptions.length < paginationFilters.limit}
                  className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                >
                  Suivant
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </button>
              </div>

              {/* Informations de pagination */}
              <div className="flex items-center text-sm text-gray-700">
                <span className="mr-2">
                  Affichage de {paginationFilters.skip + 1} à {paginationFilters.skip + souscriptions.length}
                </span>
                {souscriptions.length > 0 && (
                  <span className="text-gray-500">
                    | Page {Math.floor(paginationFilters.skip / paginationFilters.limit) + 1}
                  </span>
                )}
                {souscriptions.length === paginationFilters.limit && (
                  <span className="ml-2 text-blue-600 text-xs">
                    (Il peut y avoir plus de résultats)
                  </span>
                )}
              </div>

              {/* Sélecteur de limite et filtre statut */}
              <div className="flex items-center space-x-4">
                {/* Filtre par statut */}
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-700">Statut:</label>
                  <select
                    value={paginationFilters.statut}
                    onChange={(e) => handlePaginationChange('statut', e.target.value)}
                    className="text-sm border border-gray-300 rounded px-2 py-1"
                  >
                    <option value="">Tous</option>
                    <option value="ATTENTE_PAIEMENT">En attente paiement</option>
                    <option value="ATTENTE_LIVRAISON">En attente livraison</option>
                    <option value="LIVRE">Livré</option>
                    <option value="CLOTURE">Clôturé</option>
                    <option value="SUPPRIMEE">Supprimée</option>
                  </select>
                </div>

                {/* Sélecteur de limite */}
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-700">Afficher:</label>
                  <select
                    value={paginationFilters.limit}
                    onChange={(e) => handlePaginationChange('limit', parseInt(e.target.value))}
                    className="text-sm border border-gray-300 rounded px-2 py-1"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-sm text-gray-700">par page</span>
                </div>
              </div>
            </div>

            {/* Version mobile/tablette */}
            <div className="lg:hidden space-y-3">
              {/* Ligne 1: Boutons navigation */}
              <div className="flex justify-between items-center">
                <button
                  onClick={() => handlePaginationChange('skip', Math.max(0, paginationFilters.skip - paginationFilters.limit))}
                  disabled={paginationFilters.skip === 0}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                  </svg>
                  Précédent
                </button>
                <button
                  onClick={() => handlePaginationChange('skip', paginationFilters.skip + paginationFilters.limit)}
                  disabled={souscriptions.length < paginationFilters.limit}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                >
                  Suivant
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </button>
              </div>

              {/* Ligne 2: Informations de pagination */}
              <div className="text-center">
                <div className="text-sm text-gray-700">
                  <span>Affichage de {paginationFilters.skip + 1} à {paginationFilters.skip + souscriptions.length}</span>
                  {souscriptions.length > 0 && (
                    <span className="text-gray-500 ml-2">
                      • Page {Math.floor(paginationFilters.skip / paginationFilters.limit) + 1}
                    </span>
                  )}
                </div>
                {souscriptions.length === paginationFilters.limit && (
                  <div className="text-blue-600 text-xs mt-1">
                    (Il peut y avoir plus de résultats)
                  </div>
                )}
              </div>

              {/* Ligne 3: Filtres */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                {/* Filtre par statut */}
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-700">Statut:</label>
                  <select
                    value={paginationFilters.statut}
                    onChange={(e) => handlePaginationChange('statut', e.target.value)}
                    className="text-sm border border-gray-300 rounded px-2 py-1"
                  >
                    <option value="">Tous</option>
                    <option value="ATTENTE_PAIEMENT">En attente paiement</option>
                    <option value="ATTENTE_LIVRAISON">En attente livraison</option>
                    <option value="LIVRE">Livré</option>
                    <option value="CLOTURE">Clôturé</option>
                    <option value="SUPPRIMEE">Supprimée</option>
                  </select>
                </div>

                {/* Sélecteur de limite */}
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-700">Afficher:</label>
                  <select
                    value={paginationFilters.limit}
                    onChange={(e) => handlePaginationChange('limit', parseInt(e.target.value))}
                    className="text-sm border border-gray-300 rounded px-2 py-1"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-sm text-gray-700">par page</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {restoreModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-200 bg-blue-50 rounded-t-lg">
              <h3 className="text-lg font-semibold text-gray-900">Associer un logement disponible</h3>
              <p className="text-sm text-gray-600 mt-1">Souscription {restoreModal.souscription?.reference}</p>
            </div>
            <div className="p-6 space-y-4">
              {restoreModal.errorMessage && (
                <div className="bg-orange-50 border border-orange-200 text-orange-800 rounded-md p-3 text-sm">
                  {restoreModal.errorMessage}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nouveau logement</label>
                <select
                  value={restoreModal.selectedLogementId}
                  onChange={(e) => setRestoreModal(prev => ({ ...prev, selectedLogementId: e.target.value, errorMessage: '' }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={restoreModal.loading}
                >
                  <option value="">Sélectionner un logement disponible</option>
                  {restoreModal.logements.map((logement) => (
                    <option key={logement.id} value={logement.id}>
                      {logement.titre} - {logement.ville} {logement.code_postal || ''}
                    </option>
                  ))}
                </select>
              </div>
              {restoreModal.logements.length === 0 && (
                <p className="text-sm text-red-600">Aucun logement disponible pour le moment.</p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end space-x-3">
              <button
                onClick={() => setRestoreModal({ show: false, souscription: null, logements: [], selectedLogementId: '', errorMessage: '', loading: false })}
                disabled={restoreModal.loading}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleRestoreWithLogement}
                disabled={restoreModal.loading || restoreModal.logements.length === 0}
                className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 flex items-center space-x-2"
              >
                {restoreModal.loading && <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>}
                <span>Rétablir</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Voir Détails */}
      <SouscriptionViewModal
        souscription={selectedSouscription}
        show={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedSouscription(null);
        }}
      />

      {/* Wizard Edition */}
      {showEditWizard && souscriptionToEdit && (
        <WizardSouscription
          souscriptionToEdit={souscriptionToEdit}
          onComplete={handleEditComplete}
          onCancel={handleEditCancel}
        />
      )}

      {/* Modal Envoi Email */}
      <ConfirmModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onConfirm={emailModalConfig.onConfirm}
        title={emailModalConfig.title}
        message={emailModalConfig.message}
        type={emailModalConfig.type}
        loading={emailLoading}
        confirmText={emailModalConfig.type === 'success' ? 'OK' : emailModalConfig.type === 'error' ? 'OK' : 'Envoyer'}
        cancelText="Annuler"
      />

      {/* Modal Choix de Méthode de Paiement */}
      <PaymentMethodModal
        souscription={souscriptionToPay}
        show={showPaymentMethodModal}
        onClose={handlePaymentMethodModalClose}
        onSelectMethod={handlePaymentMethodSelect}
      />

      {/* Modal Paiement Manuel */}
      <PaiementModal
        souscription={souscriptionToPay}
        show={showPaiementModal}
        onClose={handlePaiementModalClose}
        onSuccess={handlePaiementSuccess}
      />

      {/* Modals de Livraison */}
      <LivraisonLoader 
        show={showLivraisonLoader}
        souscriptionReference={livraisonData.souscriptionReference}
      />
      
      <LivraisonSuccessModal 
        show={showLivraisonSuccess}
        onClose={() => setShowLivraisonSuccess(false)}
        souscriptionReference={livraisonData.souscriptionReference}
        documentPath={livraisonData.documentPath}
        downloadFilename={livraisonData.downloadFilename}
      />
      
      <LivraisonErrorModal 
        show={showLivraisonError}
        onClose={() => setShowLivraisonError(false)}
        souscriptionReference={livraisonData.souscriptionReference}
        errorMessage={livraisonData.errorMessage}
      />

      {/* Modal de confirmation de livraison */}
      <ConfirmationModal
        show={showLivraisonConfirm}
        onClose={() => {
          setShowLivraisonConfirm(false);
          setSouscriptionToDeliver(null);
        }}
        onConfirm={confirmLivraison}
        title="Confirmer la livraison"
        message={`Êtes-vous sûr de vouloir livrer la souscription ${souscriptionToDeliver?.reference} ? Cette action générera l'attestation d'hébergement et enverra automatiquement le document par email au client.`}
        confirmText="Livrer"
        cancelText="Annuler"
        type="warning"
      />

      {/* Modal d'erreur de paiement */}
      <PaymentErrorModal
        show={showPaymentError}
        onClose={() => setShowPaymentError(false)}
        errorType={paymentErrorData.errorType}
        souscriptionReference={paymentErrorData.souscriptionReference}
        errorMessage={paymentErrorData.errorMessage}
      />
    </>
  );
};

export default HistoriqueSection;
